import { RawLocation } from "vue-router";
import { updateState } from "@/util/updateCheck";

/**
 * Contracts for the guided tour. Types and a handful of constants only -- nothing here imports
 * driver.js, so the welcome modal in the main bundle can share `canRunTour()` with the tour hub
 * without pulling the lazy tour chunk into the main one.
 */

/** Bootstrap `lg`. Below it the sidebar is off-canvas and the top nav collapses, so no run may start. */
export const TOUR_MIN_WIDTH = 992;

/** Anchor wait budget at step start, and the grace period when an anchor vanishes mid-step. */
export const TOUR_ANCHOR_WAIT_MS = 3000;

export const TOUR_WIDTH_NOTE = `The guided tour needs a window at least ${TOUR_MIN_WIDTH}px wide`;
export const TOUR_UPDATE_NOTE = "Refresh to load the latest version";

export type TourBlockedReason = "width" | "update";

/**
 * Why a run cannot start right now, or null when it can. The tour hub and the welcome modal both
 * call this to disable their buttons and show the matching note, so the two never disagree.
 *
 * Width is reported first: a narrow window makes the tour unable to work at all, while a pending
 * update only makes it unwise to start one.
 *
 * `width` is a parameter because `window.innerWidth` is not reactive: a component tracking it on a
 * resize listener passes its own value so its button re-enables as the window grows.
 */
export function tourBlockedReason(width?: number): TourBlockedReason | null {
    const innerWidth = width ?? (typeof window === "undefined" ? TOUR_MIN_WIDTH : window.innerWidth);
    if (innerWidth < TOUR_MIN_WIDTH) return "width";
    // A deploy landing mid-run would hard-navigate the page and cut the run short.
    if (updateState.updateAvailable) return "update";
    return null;
}

export function canRunTour(width?: number): boolean {
    return tourBlockedReason(width) === null;
}

export function tourBlockedNote(reason: TourBlockedReason | null): string {
    if (reason === "width") return TOUR_WIDTH_NOTE;
    if (reason === "update") return TOUR_UPDATE_NOTE;
    return "";
}

/**
 * The selector for an anchor, optionally narrowed by its key. Handed to driver.js as a string so it
 * re-resolves the element after a re-render, and used by the host to find a trigger to click.
 */
export function tourAnchorSelector(anchor: string, anchorKey?: string): string {
    const base = `[data-tour="${anchor}"]`;
    return anchorKey ? `${base}[data-tour-key="${anchorKey}"]` : base;
}

export interface TourContext {
    /** Vue.prototype.$canNavigateTo */
    canNavigateTo: (location: RawLocation) => boolean;
    /** Case-insensitive, like the router guard. */
    hasPermission: (code: string) => boolean;
    sandboxMode: boolean;
    /** tenantConfig.disableGuidedTour */
    tourDisabled: boolean;
    /** For `when` rules on configuration flags, e.g. enableAR. Only the fields `tenantConfigFields()` names. */
    tenantConfig: Record<string, unknown>;
    /** Tenant page names, e.g. the print queue's. */
    pageNames: Record<string, string>;
}

export type AdvanceOn = "next" | "click" | "anchorAppears";

export interface TourStep {
    id: string;
    /**
     * `data-tour` value, handed to driver.js as a selector so it re-resolves after a re-render.
     * Omitted = a centered popover with no highlight.
     */
    anchor?: string;
    /** Narrows a repeated anchor, e.g. `sidebar.item` by path. */
    anchorKey?: string;
    title: string;
    /** Markdown, rendered at run time. Links open in a new tab. */
    body: string;
    side?: "top" | "right" | "bottom" | "left";
    /**
     * Where the popover sits along that side. `"end"` is what a step pointing at a search control
     * wants: the list drops the full width of the page with its text on the left, so a popover
     * left-aligned under it covers the very options the step is asking the user to pick from.
     */
    align?: "start" | "center" | "end";
    /** Navigate before this step; skipped unless `canNavigateTo` passes. */
    route?: RawLocation;
    /** Skipped unless the user holds one. Required for gates that exist only on the server. */
    anyPermission?: string[];
    /** Any other skip rule. */
    when?: (ctx: TourContext) => boolean;
    /** Default false: a disabled anchor counts as missing. */
    allowDisabled?: boolean;
    /** Default true; standard modules set false on Save/Print/Remove-style controls. */
    interactive?: boolean;
    /** Anchor of a trigger to click first (dropdown, tab, collapse); closed again on leave. */
    open?: string;
    /**
     * Default 'next'. 'click' = the highlighted element is clicked (sandbox only, on write
     * actions); 'anchorAppears' = `advanceAnchor` shows up, with no timeout and Next hidden.
     */
    advanceOn?: AdvanceOn;
    advanceAnchor?: string;
    advanceAnchorKey?: string;
    /** Default TOUR_ANCHOR_WAIT_MS; applies at step start and when the anchor vanishes mid-step. */
    waitForMs?: number;
    beforeShow?: (host: TourHost, ctx: TourContext) => void | Promise<void>;
}

export interface TourModule {
    /** 'basics', 'patient-dashboard', 'sandbox-programs'. */
    id: string;
    title: string;
    /**
     * Overrides `title` for an area a tenant can rename, e.g. the print queue. `title` stays the
     * fallback, so sorting, specs and any caller without a context still have a plain string.
     */
    titleFor?: (ctx: TourContext) => string;
    description: string;
    /** basics 10, standard 20-90, sandbox 100-190. */
    order: number;
    mode: "standard" | "sandbox" | "both";
    /** TOUR_VERSION when the module was added; drives the New badge. */
    sinceVersion: number;
    /** Eligibility = ctx.canNavigateTo(route). */
    route?: RawLocation;
    /** Extra gate for features with no route of their own. */
    anyPermission?: string[];
    /** At most 8, bar the exceptions listed in anchors.spec.ts, which enforces it. */
    steps: TourStep[];
}

/** A module's name for this tenant. The hub and the chained hand-off note both read it from here. */
export function moduleTitle(module: TourModule, ctx: TourContext): string {
    return module.titleFor?.(ctx) ?? module.title;
}

/** Implemented by DashboardLayout so steps can prepare the page. */
export interface TourHost {
    /** Remembers sidebar minimized/open state at run start. */
    snapshotLayout(): void;
    /** Puts it back when the run ends or is cancelled. */
    restoreLayout(): void;
    /** Un-minimizes on desktop, opens the off-canvas sidebar on mobile. */
    openSidebar(): void;
    /** Expands the owning group and scrolls the item into view. */
    revealNavItem(path: string): void;
    /** Focuses the global search box. Never fills it in -- the query stays the user's. */
    focusGlobalSearch(): void;
    /** Clicks the trigger, waits a frame. */
    open(anchor: string, anchorKey?: string): Promise<void>;
    /** Reverses `open`; the service calls it before the next step opens anything. */
    close(anchor: string, anchorKey?: string): void;
    /** Called before the first step of every run. */
    exitAutoMode(): void;
    /** $watch on $root.$data.inactivityState.phase; returns the unwatch. */
    watchInactivity(onLeaveActive: () => void): () => void;
}
